import { useEffect, useRef } from 'react';

/**
 * ScrollSideLine — fil terracotta vertical fixed left-0 qui se trace au scroll +
 * petit point lumineux "tête de progression" qui descend selon scrollYProgress.
 *
 * Pattern porté depuis equipe-buteau (ScrollSideBronzeLine) adapté Océanne.
 *
 * Phase 27.73 perf optim : refactor useState → useRef + manipulation DOM directe.
 * Avant : setProgress() à chaque scroll frame → React reconciliation 60×/sec sur
 * 3 éléments (rail + fil + head).
 * Après : 2 refs (fil + head) + .style.transform/opacity direct → GPU compositing,
 * zero React reconciliation. Pattern équivalent à motion/react useScroll Buteau/Serujan.
 *
 * Desktop only (`hidden md:block`). Position left-0 = ne conflict pas avec
 * SectionRail (left-6) ni Hero content (px-20).
 */
export function ScrollSideLine() {
  const filRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
        // Head opacity : fade-in 0-2% scroll, fade-out 98-100% (évite point isolé au top/bottom).
        const headOpacity =
          ratio < 0.02 ? ratio / 0.02 : ratio > 0.98 ? (1 - ratio) / 0.02 : 1;

        const fil = filRef.current;
        if (fil) fil.style.transform = `scaleY(${ratio})`;

        const head = headRef.current;
        if (head) {
          head.style.transform = `translateY(${ratio * 100}vh)`;
          head.style.opacity = String(headOpacity);
        }
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    const timeouts = [500, 1500, 2500].map((d) => window.setTimeout(onScroll, d));
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return (
    <>
      {/* Rail terracotta discret — toute la hauteur du viewport.
          Phase 27.72F-bis : opacity 0.22 + width 2px pour visibilité sur cream Océanne. */}
      <div
        className="fixed top-0 left-0 z-[40] hidden h-screen w-[2px] pointer-events-none md:block"
        style={{ backgroundColor: 'oklch(0.62 0.135 38 / 0.22)' }}
        aria-hidden="true"
      />
      {/* Fil terracotta — useRef bypass React, .style.transform écrit GPU direct. */}
      <div
        ref={filRef}
        className="fixed top-0 left-0 z-[40] hidden h-screen w-[2px] origin-top pointer-events-none transition-transform duration-150 ease-out motion-reduce:transition-none md:block"
        style={{
          transform: 'scaleY(0)',
          background:
            'linear-gradient(to bottom, oklch(0.62 0.135 38) 0%, oklch(0.62 0.135 38 / 0.75) 50%, oklch(0.62 0.135 38 / 0.35) 100%)',
        }}
        aria-hidden="true"
      />
      {/* Tête lumineuse — point terracotta centered sur rail (left:-4px + w-2.5).
          useRef : .style.transform + .style.opacity écrits direct au scroll. */}
      <div
        ref={headRef}
        className="fixed top-0 z-[40] hidden h-2.5 w-2.5 rounded-full pointer-events-none transition-[transform,opacity] duration-150 ease-out motion-reduce:transition-none md:block"
        style={{
          left: '-4px',
          transform: 'translateY(0vh)',
          opacity: 0,
          backgroundColor: 'oklch(0.62 0.135 38)',
          boxShadow:
            '0 0 12px 3px oklch(0.62 0.135 38 / 0.65), 0 0 24px 6px oklch(0.62 0.135 38 / 0.25)',
        }}
        aria-hidden="true"
      />
    </>
  );
}
